<template>
    <div class="report__dashboard_show_digitallearning">
        <div class="report__dashboard_show_digitallearning_header">
            <component-filters-dashboard-reports
                ref="reportfilters"
                :filter-key="'digital_learning'"
                @emit-submit-request="performGetData"
                @emit-get-request="performGetData"
            ></component-filters-dashboard-reports>
        </div>
        <div class="report__dashboard_show_digitallearning_main">
            <div class="container">
                <template v-if="requestPending">
                    <component-loading :size="'small'"></component-loading>
                </template>
                <template v-else>
                    <div class="columns is-block-mobile columns--margin-1-mobile is-variable">
                        <div class="column">
                            <component-title
                                :size="'large'"
                                :underline="false"
                                :raw="true"
                            >Course enrolment status
                            </component-title>
                        </div>
                        <div class="column is-narrow">
                            <component-button
                                :name="'report-dashboard-show-learning-activity-selectcourses'"
                                :align="'left'"
                                @click="openCourseSelectionModal"
                            >Select courses
                            </component-button>
                        </div>
                    </div>
                    <template v-if="digitalLearningData.categories.length && digitalLearningFilterCourses.length">
                        <component-chart
                            ref="chartcoursecompletions"
                            :class="'report__dashboard_show_digitallearning_main_diagram'"
                            :name="'report-dashboard-digital-learning'"
                            :type="'bar'"
                            :height="`${digitalLearningData.categories.length * 80 + 70}px`"
                            :series="digitalLearningData.series"
                            :stacked="true"
                            :data-labels="{
                                    enabled: true
                                }"
                            :colors="['#0074D9', '#FF6105', '#63DD96']"
                            :plot-options="{
                                bar: {
                                    horizontal: true
                                }
                            }"
                            :tooltip="{
                                y: {
                                    formatter (val) {
                                        return val.toFixed(0)
                                    }
                                }
                            }"
                            :xaxis="{
                                type: 'category',
                                categories: digitalLearningData.categories,
                                labels: {
                                    formatter (val) {
                                        return val.toFixed(0)
                                    }
                                }
                            }"
                            @emit-data-point-selection="handleGenerateEnrolmentsReport"
                        ></component-chart>
                    </template>
                    <template v-else-if="!digitalLearningData.categories.length && digitalLearningFilterCourses.length">
                        <component-empty-state
                            :title="'No results'"
                            :submit-button-active="false"
                        ></component-empty-state>
                    </template>
                    <template v-else>
                        <component-empty-state
                            :title="'Choose to add all or a selection of your courses here to visualise learner progress. \n Go ahead and add a course to this report.'"
                            :submit-button-active="true"
                            :submit-button-text="'Add course'"
                            @emit-submit="openCourseSelectionModal"
                        ></component-empty-state>
                    </template>
                </template>
            </div>
        </div>
        <pop-table
            ref="courseselectionpoptable"
            :size="'full'"
            :class="'report__dashboard_show_digitallearning_courseselectionspoptable'"
            :content-text="''"
            :cancel-button-text="'Close'"
            :eventbus-emit-name="'course-selection'"
            :filter-search-placeholder="'Search courses'"
            :filter-search-queryable="true"
            :filter-select-active="true"
            :filter-select-items="courseSelectionPopTableFilterItems"
            :get-pagination-getter="'getEbsCoursesPagination'"
            :get-items-getter="'getEbsCoursesMappedIdNameStatusLabels'"
            :get-items-getter-mapped="'getEbsCoursesMappedLabelValue'"
            :get-items-dispatch="'ebsCoursesGetAdmin'"
            :get-items-dispatch-trigger-name="'trigger-get-courses'"
            :item-name="['course', 'courses']"
            :submit-button-text="'Select'"
            :title-text="'Select courses'"
            :labels="[
                {
                    key: 'name',
                    title: 'Course name',
                    type: 'string',
                    sortable: true,
                    queryable: true
                },
                {
                    key: 'labels',
                    title: 'Labels',
                    type: 'array',
                    subType: 'object',
                    sortable: false,
                    queryable: false
                }
            ]"
            :table-checkbox="true"
            :table-checkbox-name="'course'"
            :preselected="digitalLearningFilterCourses"
            @emit-submit="handleSetCoursesSelection"
        ></pop-table>
    </div>
</template>

<script>
import ComponentFiltersDashboardReports from '@/components/default/shared/ComponentFiltersDashboardReports'
import ComponentLoading from '@/components/default/shared/ComponentLoading'
import ComponentEmptyState from '@/components/default/shared/ComponentEmptyState'
import ComponentChart from '@/components/default/shared/ComponentChart'
import PopTable from '@/components/default/shared/PopTable'
import ComponentButton from '@/components/default/shared/ComponentButton'
import ComponentTitle from '@/components/default/shared/ComponentTitle'

export default {
  name: 'report-dashboard-show-digital-learning',
  components: { ComponentTitle, ComponentButton, PopTable, ComponentChart, ComponentEmptyState, ComponentLoading, ComponentFiltersDashboardReports },
  data () {
    return {
      requestPending: true,
      filterSearch: ''
    }
  },
  computed: {
    digitalLearningData () {
      return this.$store.getters.getEbsReportDashboardDataDigitalLearning
    },
    digitalLearningFilterCourses () {
      return this.$store.getters.getEbsReportDashboardFiltersPermanentDigitalLearningCourses
    },
    courseSelectionPopTableFilterItems () {
      return [
        {
          key: 'labels',
          label: 'Labels',
          type: 'multiselect',
          getItemsGetter: 'getEbsModuleCustomLabelsMappedIdNameStatus',
          getItemsGetterMapped: 'getEbsModuleCustomLabelsMappedLabelValue',
          getItemsDispatch: 'ebsModuleGetLabels',
          getPaginationGetter: 'getEbsModuleCustomLabelsPagination',
          itemName: ['label', 'labels'],
          searchQueryable: true,
          tableLabels: [
            {
              key: 'name',
              title: 'Name',
              type: 'string',
              sortable: true,
              queryable: true
            }
          ]
        }
      ]
    }
  },
  methods: {
    openCourseSelectionModal () {
      const preselectedCourses = this.digitalLearningFilterCourses
      this.$refs.courseselectionpoptable.openPop()
      this.$refs.courseselectionpoptable.setPreselected(preselectedCourses)
    },
    performGetData () {
      if (this.digitalLearningFilterCourses.length) {
        this.requestPending = true
        this.$store.dispatch('ebsReportDashboardGetDataDigitalLearning').finally(() => {
          this.requestPending = false
        })
      } else {
        this.requestPending = false
      }
    },
    handleSetCoursesSelection (data) {
      this.$store.dispatch('ebsReportDashboardSetFilters', {
        filterKey: 'digital_learning',
        type: 'permanent',
        data: {
          key: 'courses',
          values: data
        }
      })
      this.$refs.reportfilters.handleFiltersSelectSubmit()
    },
    handleGenerateEnrolmentsReport (data) {
      const coursesList = this.digitalLearningData.rawData
      const seriesIndex = data.seriesIndex
      const dataPointIndex = data.dataPointIndex
      const catalogItem = {
        name: coursesList[dataPointIndex].name,
        id: coursesList[dataPointIndex].id
      }
      let enrolmentState

      switch (seriesIndex) {
        case 0:
          enrolmentState = 'enrolled'
          break
        case 1:
          enrolmentState = 'in_progress'
          break
        case 2:
          enrolmentState = 'completed'
          break
      }

      this.$store.dispatch('ebsReportDashboardGetDataDigitalLearningEnrolments', { enrolmentState, catalogItem })
    }
  }
}
</script>

<style lang="scss">
    @import "~@/assets/scss/components/ebs/reports/_reportdashboarddigitallearning.scss";
</style>
